import React, { useCallback, useState } from "react";
import { WizardData } from "../index";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import {
  validateImageFileAsync,
  MIN_LISTING_IMAGE_SHORT_EDGE,
  compressImage,
} from "../../../utils/imageCompression";
import {
  uploadFile,
  verifyTitleDocument,
} from "../../../services/uploads";
import {
  MIN_LISTING_PHOTOS,
  MAX_LISTING_PHOTOS,
  MAX_LISTING_VIDEOS,
  MAX_VIDEO_SECONDS,
  MAX_VIDEO_BYTES,
  ACCEPTED_VIDEO_ACCEPT,
} from "../../../constants/listingMedia";
import {
  validateVideoFileAsync,
  formatVideoDuration,
} from "../../../utils/videoValidation";
import VideocamIcon from "@mui/icons-material/Videocam";

interface PhotosStepProps {
  data: WizardData;
  updateData: (updates: Partial<WizardData>) => void;
  onNext: () => void;
  onBack: () => void;
  darkMode: boolean;
}

const MIN_PHOTOS = MIN_LISTING_PHOTOS;

const PhotosStep: React.FC<PhotosStepProps> = ({
  data,
  updateData,
  onNext,
  onBack,
  darkMode,
}) => {
  const photoCount = data.vehicleImages.length;
  const videoCount = data.vehicleVideos.length;
  const meetsMinimum = photoCount >= MIN_PHOTOS;
  const canAddPhoto = photoCount < MAX_LISTING_PHOTOS;
  const canAddVideo = videoCount < MAX_LISTING_VIDEOS;
  const [titleBusy, setTitleBusy] = useState(false);

  const handlePhotoSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      const input = e.target;
      if (!files?.length) {
        input.value = "";
        return;
      }

      const room = MAX_LISTING_PHOTOS - data.vehicleImages.length;
      const newFiles = Array.from(files).slice(0, Math.max(0, room));
      const validFiles: File[] = [];
      let invalidCount = 0;

      for (const file of newFiles) {
        const validation = await validateImageFileAsync(file, {
          minShortEdge: MIN_LISTING_IMAGE_SHORT_EDGE,
        });
        if (validation.valid) {
          validFiles.push(file);
        } else {
          invalidCount += 1;
        }
      }

      if (invalidCount > 0) {
        alert(
          `1 or more images are invalid. Vehicle photos must be at least ${MIN_LISTING_IMAGE_SHORT_EDGE}px on the shortest side.`
        );
      }
      if (files.length > room) {
        alert(`You can add at most ${MAX_LISTING_PHOTOS} photos per listing.`);
      }

      if (validFiles.length > 0) {
        updateData({
          vehicleImages: [...data.vehicleImages, ...validFiles],
        });
      }

      input.value = "";
    },
    [data.vehicleImages, updateData]
  );

  const handleVideoSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      const input = e.target;
      if (!files?.length) {
        input.value = "";
        return;
      }

      const room = MAX_LISTING_VIDEOS - data.vehicleVideos.length;
      const candidates = Array.from(files).slice(0, Math.max(0, room));
      const valid: WizardData["vehicleVideos"] = [];
      const errors: string[] = [];

      for (const file of candidates) {
        const result = await validateVideoFileAsync(file);
        if (result.valid) {
          valid.push({
            file,
            durationSeconds: result.durationSeconds,
            contentType: result.contentType,
          });
        } else {
          errors.push(`${file.name}: ${result.reason}`);
        }
      }

      if (files.length > room) {
        errors.push(
          `You can add at most ${MAX_LISTING_VIDEOS} videos per listing.`
        );
      }
      if (errors.length > 0) alert(errors.join("\n"));
      if (valid.length > 0) {
        updateData({
          vehicleVideos: [...data.vehicleVideos, ...valid],
        });
      }
      input.value = "";
    },
    [data.vehicleVideos, updateData]
  );

  const handleRemoveVideo = useCallback(
    (index: number) => {
      updateData({
        vehicleVideos: data.vehicleVideos.filter((_, i) => i !== index),
      });
    },
    [data.vehicleVideos, updateData]
  );

  const handleRemovePhoto = useCallback(
    (index: number) => {
      const newImages = data.vehicleImages.filter((_, i) => i !== index);
      updateData({ vehicleImages: newImages });
    },
    [data.vehicleImages, updateData]
  );

  const handleSetMainPhoto = useCallback(
    (index: number) => {
      if (index === 0) return; // Already main
      const newImages = [...data.vehicleImages];
      const [selected] = newImages.splice(index, 1);
      newImages.unshift(selected);
      updateData({ vehicleImages: newImages });
    },
    [data.vehicleImages, updateData]
  );

  const handleTitleSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      const input = e.target;
      input.value = "";
      if (!file) return;

      // Images only for MVP (server MIME allowlist has no PDF).
      if (file.type && !file.type.startsWith("image/")) {
        alert("Please upload a photo of your title (JPEG, PNG, or WebP).");
        return;
      }

      setTitleBusy(true);
      updateData({
        titleImage: file,
        titleImageKey: null,
        titleVerifyStatus: "checking",
        titleVerifyMessage: "Checking title document…",
      });

      try {
        const compressed = await compressImage(file);
        const key = await uploadFile(compressed, "title");
        const result = await verifyTitleDocument({
          titleImageKey: key,
          vin: data.vin,
        });
        updateData({
          titleImage: file,
          titleImageKey: key,
          titleVerifyStatus: result.status,
          titleVerifyMessage: result.message,
        });
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Title verification failed. You can continue without the Title on File badge.";
        updateData({
          titleImage: file,
          titleImageKey: null,
          titleVerifyStatus: "failed",
          titleVerifyMessage: message,
        });
      } finally {
        setTitleBusy(false);
      }
    },
    [data.vin, updateData]
  );

  const handleRemoveTitle = useCallback(() => {
    updateData({
      titleImage: null,
      titleImageKey: null,
      titleVerifyStatus: "idle",
      titleVerifyMessage: null,
    });
  }, [updateData]);

  const handleNext = () => {
    if (!meetsMinimum) {
      alert(`Please upload at least ${MIN_PHOTOS} photos of your vehicle`);
      return;
    }
    if (titleBusy || data.titleVerifyStatus === "checking") {
      alert("Please wait for title document verification to finish.");
      return;
    }
    onNext();
  };

  const titleStatus = data.titleVerifyStatus;
  const titlePanelBg =
    titleStatus === "passed"
      ? darkMode
        ? "#1b3d1f"
        : "#e8f5e9"
      : titleStatus === "checking"
        ? darkMode
          ? "#1a2a3d"
          : "#e3f2fd"
        : titleStatus === "failed" || titleStatus === "unavailable"
          ? darkMode
            ? "#3d2e1a"
            : "#fff3e0"
          : darkMode
            ? "#1a1a1a"
            : "#fafafa";

  const titleStatusColor =
    titleStatus === "passed"
      ? darkMode
        ? "#81c784"
        : "#2e7d32"
      : titleStatus === "checking"
        ? darkMode
          ? "#90caf9"
          : "#1565c0"
        : darkMode
          ? "#ffb74d"
          : "#e65100";

  return (
    <div className="step-container">
      <h2 className="step-title">Upload Photos & Videos</h2>
      <p className="step-subtitle">
        High-quality photos help your listing stand out. Videos are optional.
      </p>

      {/* Requirements info */}
      <div className="photo-requirements">
        <h4>Photo & Video Requirements</h4>
        <ul>
          <li>Minimum {MIN_PHOTOS} photos required (max {MAX_LISTING_PHOTOS})</li>
          <li>Include: Front, Rear, Interior, Dashboard/Odometer</li>
          <li>At least {MIN_LISTING_IMAGE_SHORT_EDGE}px on the shortest side (width or height)</li>
          <li>Optional: up to {MAX_LISTING_VIDEOS} videos (MP4/WebM), max {MAX_VIDEO_SECONDS}s and {Math.floor(MAX_VIDEO_BYTES / (1024 * 1024))}MB each</li>
          <li>Use good lighting and clear focus</li>
          <li>Show any damage or wear honestly</li>
          <li>Click any photo to set it as the main listing image</li>
        </ul>
      </div>

      {/* Photo count indicator */}
      <div className={`photo-count ${meetsMinimum ? "met" : "not-met"}`}>
        {meetsMinimum ? (
          <CheckCircleIcon style={{ fontSize: "18px" }} />
        ) : (
          <ErrorIcon style={{ fontSize: "18px" }} />
        )}
        {photoCount} / {MIN_PHOTOS} minimum photos
        {` · ${photoCount}/${MAX_LISTING_PHOTOS} max`}
        {meetsMinimum && " ✓"}
      </div>

      {/* Photo grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        {data.vehicleImages.map((file, index) => (
          <div
            key={index}
            className={`photo-grid-item ${index === 0 ? "photo-grid-item-main" : ""}`}
            style={{
              position: "relative",
              aspectRatio: "4/3",
              borderRadius: "8px",
              overflow: "hidden",
              background: darkMode ? "#2a2a2a" : "#f0f0f0",
              border: index === 0
                ? "2px solid var(--color-primary)"
                : "2px solid transparent",
              cursor: index !== 0 ? "pointer" : "default",
            }}
            onClick={() => handleSetMainPhoto(index)}
            title={index === 0 ? "Main photo" : "Click to set as main photo"}
          >
            <img
              src={URL.createObjectURL(file)}
              alt={`Vehicle ${index + 1}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            {/* Delete button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemovePhoto(index);
              }}
              style={{
                position: "absolute",
                top: "4px",
                right: "4px",
                background: "rgba(0,0,0,0.6)",
                border: "none",
                borderRadius: "50%",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "white",
              }}
            >
              <DeleteIcon style={{ fontSize: "16px" }} />
            </button>
            {/* Main photo indicator / Set as main button */}
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleSetMainPhoto(index);
              }}
              style={{
                position: "absolute",
                bottom: "4px",
                left: "4px",
                background: index === 0 ? "var(--color-primary)" : "rgba(0,0,0,0.6)",
                color: index === 0 ? "white" : "#ccc",
                fontSize: "10px",
                padding: "2px 6px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                gap: "3px",
                cursor: index !== 0 ? "pointer" : "default",
                transition: "all 0.2s",
              }}
            >
              {index === 0 ? (
                <StarIcon style={{ fontSize: "12px" }} />
              ) : (
                <StarBorderIcon style={{ fontSize: "12px" }} />
              )}
              {index === 0 ? "Main Photo" : "Set as Main"}
            </div>
          </div>
        ))}

        {/* Add photo button */}
        {canAddPhoto && (
          <label
            style={{
              aspectRatio: "4/3",
              borderRadius: "8px",
              border: `2px dashed ${darkMode ? "#444" : "#ccc"}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              background: darkMode ? "#1a1a1a" : "#fafafa",
              color: darkMode ? "#888" : "#666",
              transition: "all 0.2s",
            }}
          >
            <AddPhotoAlternateIcon style={{ fontSize: "32px", marginBottom: "4px" }} />
            <span style={{ fontSize: "12px" }}>Add Photo</span>
            <input
              type="file"
              accept="image/*"
              multiple
              data-testid="wizard-photo-input"
              onChange={handlePhotoSelect}
              style={{ display: "none" }}
            />
          </label>
        )}
      </div>

      {/* Videos */}
      <div style={{ marginBottom: "24px" }}>
        <h4 style={{ margin: "0 0 8px", fontSize: "15px" }}>
          Videos (optional)
        </h4>
        <div className="photo-count met" style={{ marginBottom: "12px" }}>
          <VideocamIcon style={{ fontSize: "18px" }} />
          {videoCount} / {MAX_LISTING_VIDEOS} videos
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            gap: "12px",
          }}
        >
          {data.vehicleVideos.map((item, index) => (
            <div
              key={`${item.file.name}-${index}`}
              className="photo-grid-item"
              style={{
                position: "relative",
                aspectRatio: "4/3",
                borderRadius: "8px",
                overflow: "hidden",
                background: darkMode ? "#2a2a2a" : "#f0f0f0",
              }}
            >
              <video
                src={URL.createObjectURL(item.file)}
                muted
                playsInline
                preload="metadata"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <button
                type="button"
                onClick={() => handleRemoveVideo(index)}
                aria-label={`Remove video ${index + 1}`}
                style={{
                  position: "absolute",
                  top: "4px",
                  right: "4px",
                  background: "rgba(0,0,0,0.6)",
                  border: "none",
                  borderRadius: "50%",
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "white",
                }}
              >
                <DeleteIcon style={{ fontSize: "16px" }} />
              </button>
              <div
                style={{
                  position: "absolute",
                  bottom: "4px",
                  left: "4px",
                  background: "rgba(0,0,0,0.65)",
                  color: "white",
                  fontSize: "10px",
                  padding: "2px 6px",
                  borderRadius: "4px",
                }}
              >
                {formatVideoDuration(item.durationSeconds) || "Video"}
              </div>
            </div>
          ))}
          {canAddVideo && (
            <label
              style={{
                aspectRatio: "4/3",
                borderRadius: "8px",
                border: `2px dashed ${darkMode ? "#444" : "#ccc"}`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                background: darkMode ? "#1a1a1a" : "#fafafa",
                color: darkMode ? "#888" : "#666",
              }}
            >
              <VideocamIcon style={{ fontSize: "32px", marginBottom: "4px" }} />
              <span style={{ fontSize: "12px" }}>Add Video</span>
              <input
                type="file"
                accept={ACCEPTED_VIDEO_ACCEPT}
                multiple
                data-testid="wizard-video-input"
                onChange={handleVideoSelect}
                style={{ display: "none" }}
              />
            </label>
          )}
        </div>
      </div>

      {/* Title upload section (optional) */}
      <div className="title-upload-section">
        <div className="title-upload-header">
          <h3 style={{ margin: 0, fontSize: "16px", color: darkMode ? "#fff" : "#333" }}>
            Title Document
          </h3>
          <span className="title-badge">Optional</span>
        </div>
        <p style={{
          fontSize: "13px",
          color: darkMode ? "#888" : "#666",
          marginBottom: "16px"
        }}>
          Upload a clear photo of your vehicle title to earn a &quot;Title on File&quot; badge.
          We check that the document looks like a title and that the VIN matches your listing.
        </p>

        {data.titleImage ? (
          <div
            data-testid="title-verify-panel"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "12px",
              background: titlePanelBg,
              borderRadius: "8px",
            }}
          >
            {titleStatus === "checking" || titleBusy ? (
              <AutorenewIcon
                className="title-verify-spinner"
                style={{ color: titleStatusColor }}
              />
            ) : titleStatus === "passed" ? (
              <CheckCircleIcon style={{ color: "#4caf50" }} />
            ) : (
              <DescriptionIcon style={{ color: titleStatusColor }} />
            )}
            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: 500,
                fontSize: "14px",
                color: darkMode ? "#fff" : "#333"
              }}>
                {data.titleImage.name}
              </div>
              <div
                data-testid="title-verify-message"
                style={{
                  fontSize: "12px",
                  color: titleStatusColor,
                  marginTop: "2px",
                }}
              >
                {data.titleVerifyMessage ||
                  (titleStatus === "passed"
                    ? "Title document verified"
                    : "Title document uploaded")}
              </div>
            </div>
            <button
              onClick={handleRemoveTitle}
              disabled={titleBusy || titleStatus === "checking"}
              style={{
                background: "none",
                border: "none",
                cursor: titleBusy ? "default" : "pointer",
                color: darkMode ? "#888" : "#666",
                opacity: titleBusy ? 0.5 : 1,
              }}
              aria-label="Remove title document"
            >
              <DeleteIcon />
            </button>
          </div>
        ) : (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "16px",
              border: `2px dashed ${darkMode ? "#444" : "#ccc"}`,
              borderRadius: "8px",
              cursor: "pointer",
              color: darkMode ? "#888" : "#666",
              background: darkMode ? "#1a1a1a" : "#fafafa",
            }}
          >
            <DescriptionIcon />
            <span>Upload Title Document</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/*"
              data-testid="wizard-title-input"
              onChange={handleTitleSelect}
              style={{ display: "none" }}
            />
          </label>
        )}
      </div>

      <div className="wizard-buttons">
        <button className="wizard-btn wizard-btn-secondary" onClick={onBack}>
          Back
        </button>
        <button
          className="wizard-btn wizard-btn-primary"
          onClick={handleNext}
          disabled={!meetsMinimum || titleBusy || titleStatus === "checking"}
        >
          Continue to Pricing
        </button>
      </div>
    </div>
  );
};

export default PhotosStep;
